import { Tab, Tabs } from "@mui/material";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import termsEn from "../../../../../content/legal/terms-en.md";
import termsEs from "../../../../../content/legal/terms-es.md";
import termsFr from "../../../../../content/legal/terms-fr.md";
import { Button } from "../../components/ui/Button";
import { displayToast } from "../../components/app/AppToast";
import ContentHeader from "../../components/app/headers/ContentHeader";
import LogoHeader from "../../components/app/headers/LogoHeader";
import Navbar from "../../components/app/navbars/Navbar";
import ContentMarkdown from "../../components/ContentMarkdown";
import Loader from "../../components/Loader";
import { PolicyTextStyle } from "../../components/PolicyTextStyle";
import { useActiveLang } from "../../domains/app/hooks/useActiveLang";
import { loadMarkdown } from "../../domains/app/utils/loadMarkdown";
import { iosSetBackgroundColor, isIOSApp } from "../../tools/ios";
import ScrollableLayout from "../../components/layout/ScrollableLayout";
import { useTranslation } from "react-i18next";
import { useApiQuery } from "../../services/api";
import { useFirebaseUser } from "../../domains/user/hooks/useFirebaseUser";
import { PartnerTermsContent } from "../../components/settings/partners/PartnerTermsContent";

export default function TermsPage() {
  const history = useHistory();
  const { activeLang } = useActiveLang();
  const { t } = useTranslation();
  const { isAuthenticatedFirebase: isAuthenticated } = useFirebaseUser();
  const [markdownContent, setMarkdownContent] = useState<string | null>(null);
  const searchParams = new URLSearchParams(history.location.search);
  const hideTitle = searchParams.get("hideTitle") !== null;
  const isPush = searchParams.get("isPush") !== null;
  const activeTab = searchParams.get("tab") === "partners" ? 1 : 0;

  const userQuery = useApiQuery(
    "/webapp/private/users/me",
    isAuthenticated
      ? { query: { language: activeLang }, keepPreviousData: true }
      : false
  );

  const handleTabChange = (_: React.SyntheticEvent, newValue: number): void => {
    const newSearchParams = new URLSearchParams(history.location.search);
    newSearchParams.set("tab", newValue === 1 ? "partners" : "juisci");
    history.replace({
      pathname: history.location.pathname,
      search: newSearchParams.toString(),
    });
  };

  useEffect(() => {
    initialize();
  }, [activeLang]);

  async function initialize() {
    try {
      if (isIOSApp()) iosSetBackgroundColor("#FFFFFF");

      const md =
        activeLang === "fr" ? termsFr : activeLang === "es" ? termsEs : termsEn;

      setMarkdownContent(await loadMarkdown(md));
    } catch (error) {
      console.error("Couldn't load file.", error);
      displayToast(t("Something went wrong. Please try again."), "error");
    }
  }

  // Check if user has any rooms with T&C enabled
  const hasRoomsWithTerms =
    isAuthenticated &&
    userQuery.state === "success" &&
    userQuery.body.roomUserSettings.some(
      (setting) => setting.room.termsConditions?.enabled
    );

  return (
    <ScrollableLayout
      headerClassName="bg-white/90 backdrop-blur-md"
      header={
        isPush ? (
          <ContentHeader
            transparent
            title={hideTitle ? undefined : t("Terms of Use")}
          />
        ) : (
          <LogoHeader />
        )
      }
      footer={!isPush && <Navbar />}
    >
      {hasRoomsWithTerms ? (
        <div className="sticky top-0 z-10 bg-white/90 backdrop-blur-md">
          <Tabs
            variant="fullWidth"
            centered
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              "& .MuiButtonBase-root": {
                fontWeight: 400,
                fontSize: "14px",
                lineHeight: "120%",
                letterSpacing: "0.00005em",
                textTransform: "capitalize",
                color: "var(--color-secondary-bluegrey-4)",
                padding: "16px",
                borderBottom: "0.5px solid #90a4ae",
              },
              "& .MuiButtonBase-root.Mui-selected": {
                color: "var(--color-primary-black)",
              },
              "& .MuiTabs-indicator": {
                height: "1.5px",
                backgroundColor: "var(--color-primary-black)",
              },
            }}
          >
            <Tab label="Juisci" />
            <Tab label="Partners" />
          </Tabs>
        </div>
      ) : (
        <div />
      )}

      <div className="p-6">
        <div className={activeTab === 0 ? "block" : "hidden"}>
          {markdownContent ? (
            <>
              <PolicyTextStyle>
                <ContentMarkdown math={false} linkVariant="anchor">
                  {markdownContent}
                </ContentMarkdown>
              </PolicyTextStyle>

              <hr className="my-8" />

              <Button
                variant="primary"
                size="lg"
                className="w-full"
                onClick={() => {
                  history.replace("/policy?isPush=true");
                }}
              >
                {t("See our Privacy Policy")}
              </Button>
            </>
          ) : (
            <Loader />
          )}
        </div>

        {hasRoomsWithTerms && (
          <div className={activeTab === 1 ? "block" : "hidden"}>
            <PartnerTermsContent
              isLoading={userQuery.state !== "success"}
              roomUserSettings={
                userQuery.state === "success"
                  ? userQuery.body.roomUserSettings
                  : []
              }
            />
          </div>
        )}
      </div>
    </ScrollableLayout>
  );
}
